import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { projectsApi, shippingApi, usersApi, type ShippingRequest } from '@/lib/apiClient';
import { Button, StateView } from '@/components';
import ShippingDrawer, { type ShippingProjectRef } from '../shipping/ShippingDrawer';
import ShippingList from '../shipping/ShippingList';
import { ShippingStyles } from '../shipping/ShippingStyles';
import { CHANGE_NOTICE, type ShippingChange } from '../shipping/shippingForm';

/**
 * A job's Shipping tab (#717, #718, #719): ask Logistics to pick something up or deliver it, and see where each
 * request stands. Everyone who can see the job reads its requests; those who hold `receiving.edit` raise and change
 * them (the same permission as Receiving, so no new role is needed).
 *
 * Open requests come first, Expedited ones at the top: the API returns them in the order Logistics works in.
 */
export default function ShippingTab({ projectId, locked = false }: { projectId: number; locked?: boolean }) {
  const query = useQuery({
    queryKey: ['shipping', projectId],
    queryFn: () => shippingApi.list({ projectId }),
  });

  // The job's identity, to pin a new request to it. Shares the workspace's warm ['project', id] cache.
  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId) as Promise<ShippingProjectRef>,
    enabled: Number.isFinite(projectId),
    staleTime: 60_000,
  });
  const thisProject: ShippingProjectRef = {
    id: projectId,
    jobNumber: projectQuery.data?.jobNumber ?? '',
    title: projectQuery.data?.title ?? '',
  };

  // Mirrors the API: writing needs receiving.edit (Admin passes every check). A job whose activity is locked (no
  // Sage job number yet) takes no new requests, so it is read-only for everyone.
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  const mayEdit = (me?.roles.includes('Admin') ?? false) || (me?.permissions.includes('receiving.edit') ?? false);
  const canEdit = mayEdit && !locked;

  const [drawer, setDrawer] = useState<{ item: ShippingRequest | null } | null>(null);
  const [showClosed, setShowClosed] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const onSaved = (change: ShippingChange) => { setDrawer(null); setNotice(CHANGE_NOTICE[change]); };

  return (
    <>
      <ShippingStyles />
      <div className="pw-card">
        <div className="shp-hd">
          <h2 className="pw-card-hd">Shipping</h2>
          {canEdit && <Button size="sm" onClick={() => { setNotice(null); setDrawer({ item: null }); }}>＋ Request pickup or delivery</Button>}
        </div>
        {locked && (
          <p className="shp-pad shp-muted">This job does not have its Sage job number yet, so requests cannot be added or changed until it does.</p>
        )}
        {notice && <p className="shp-ok" role="status" style={{ margin: 'var(--ew-s-3) var(--ew-s-4) 0' }}>{notice}</p>}
        <StateView
          query={query}
          empty={<>No shipping requests for this job yet.{canEdit ? ' Use Request pickup or delivery to ask Logistics to collect or deliver something.' : ''}</>}
        >
          {(rows) => {
            const open = rows.filter((r) => r.status?.phase !== 'Closed');
            const shown = showClosed ? rows : open;
            const hidden = rows.length - open.length;
            return (
              <>
                {hidden > 0 && (
                  <label className="shp-check shp-pad">
                    <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
                    Include completed and cancelled <span className="shp-hidden-count">{hidden}</span>
                  </label>
                )}
                {shown.length === 0
                  ? <p className="shp-pad shp-muted">No open requests.</p>
                  : <ShippingList requests={shown} showJob={false} onOpen={(r) => { setNotice(null); setDrawer({ item: r }); }} />}
              </>
            );
          }}
        </StateView>
      </div>

      {drawer && (
        <ShippingDrawer
          item={drawer.item}
          lockedProject={drawer.item ? undefined : thisProject}
          readOnly={!canEdit}
          onClose={() => setDrawer(null)}
          onSaved={onSaved}
        />
      )}
    </>
  );
}
